import * as React from "react";
import "../../assets/crudStyles.css";
import "./styles.css";
import { TextInputCounter } from "../../components/TextInputCounter/TextInputCounter";
import {
  required,
  ImageInput,
  Toolbar,
  SaveButton,
  useNotify,
  ImageField,
  useRecordContext,
  useEditController,
  maxLength,
  TabbedForm,
  FormTab,
} from "react-admin";
import useFileUpload from "../../hooks/useFileUpload";
import { ResourceEditor } from "../../components/ResourceEditor/ResourceEditor";

import { CreateScreen as PartnershipBrandsCreate } from "../../components/partnership-page-brands/create";
import { EditScreen as PartnershipBrandsEdit } from "../../components/partnership-page-brands/edit";

import { CreateScreen as PartnershipSectionsCreate } from "../../components/partnership-page-sections/create";
import { EditScreen as PartnershipSectionsEdit } from "../../components/partnership-page-sections/edit";
import { RaEditWrapper } from "../../components/RaEditWrapper";

interface IEdit {
  [key: string]: any;
}

const EditToolbar = ({ files, ...props }: any) => {
  return (
    <Toolbar {...props}>
      <SaveButton
        transform={({ ...data }: IEdit) => {
          const mappedRequest: any = {
            ...data,
          };

          const resourceFilesReq = {
            topSectionImageUploadId:
              files.topSectionImageUpload ?? data.topSectionImageUpload.id,
            topSubsection1ImageUploadId:
              files.topSubsection1ImageUpload ??
              data.topSubsection1ImageUpload.id,
            topSubsection2ImageUploadId:
              files.topSubsection2ImageUpload ??
              data.topSubsection2ImageUpload.id,
          };

          // Clean up fields.
          delete mappedRequest.topSectionImageUpload;
          delete mappedRequest.topSubsection1ImageUpload;
          delete mappedRequest.topSubsection2ImageUpload;
          delete mappedRequest.order;
          delete mappedRequest.id;
          delete mappedRequest.enabled;

          return {
            ...mappedRequest,
            ...resourceFilesReq,
          };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const EditScreen = (props: any) => {
  const notify = useNotify();

  // Upload Files
  const [resourceFiles, setResourceFiles] = React.useState<{
    topSectionImageUpload: null | any;
    topSubsection1ImageUpload: null | any;
    topSubsection2ImageUpload: null | any;
  }>({
    topSectionImageUpload: null,
    topSubsection1ImageUpload: null,
    topSubsection2ImageUpload: null,
  });

  const [resourceImgs, setResourceImgs] = React.useState<{
    [key: string]: any;
  }>({});

  const renderImgPreview = (name: string) => {
    if (!resourceImgs[name]) {
      return <ImageField source="uri" title="info.original_filename" />;
    }

    return (
      <FileFieldWithContext
        placeholder={
          <div className="MuiBox-root css-cb34tx-RaImageField-root">
            <img
              src={resourceImgs[name].uri}
              title={resourceImgs[name].title}
              width={200}
              height={200}
              className="RaImageField-image"
            />
          </div>
        }
      >
        <ImageField source="uri" title="title" />
      </FileFieldWithContext>
    );
  };

  // Used to track the uploaded files and show in progress or error.
  const [uploadedFileNames, setUploadFileNames] = React.useState<{
    [key: string]: string | boolean;
  }>({});

  const { uploadFile } = useFileUpload();
  const handlePrefileUpload = async (name: string, files: File[]) => {
    const fileName = files[0].name;
    notify(`Uploading file... ${fileName}`);

    setUploadFileNames({ ...uploadedFileNames, [fileName]: "Uploading..." });

    const asset = await uploadFile(files[0], name);
    if (asset !== undefined) {
      setResourceImgs({
        ...resourceImgs,
        [name]: {
          title: asset.info.original_filename,
          uri: asset.uri,
          uploading: false,
        },
      });

      const newValue = asset.id;
      setResourceFiles({ ...resourceFiles, [name]: newValue });
      notify(`Upload completed! File: ${fileName}`);
      setUploadFileNames({ ...uploadedFileNames, [fileName]: true });
    } else {
      notify(`Error uploading file! ${fileName}`);
      setUploadFileNames({
        ...uploadedFileNames,
        [fileName]: "Error uploading file, remove and try again.",
      });
    }
  };

  const FileFieldWithContext = (props: any) => {
    const { title, uri } = useRecordContext();

    if (uri) {
      return props.children;
    }

    if (!Object.hasOwn(uploadedFileNames, title)) {
      return null;
    }

    if (uploadedFileNames[title] === true) {
      return props.placeholder;
    } else {
      return <p>{uploadedFileNames[title]}</p>;
    }
  };

  // Record handler.
  const { record } = useEditController(props);

  if (record) {
    record.enabled = record.publishedAt !== null ? true : false;
  }

  return (
    <RaEditWrapper mutationMode="pessimistic">
      <TabbedForm
        toolbar={<EditToolbar files={resourceFiles} record={record} />}
      >
        <FormTab label="Top">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSectionTitle"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            source="topSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSectionImageUpload"
              label="Top Section Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSectionImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSectionImageUpload")}
            </ImageInput>
          </div>

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection1Title"
            label="Section #1 Title"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection1Description"
            label="Section #1 Description"
            multiline
            fullWidth
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSubsection1ImageUpload"
              label="Section #1 Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSubsection1ImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSubsection1ImageUpload")}
            </ImageInput>
          </div>

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection2Title"
            label="Section #2 Title"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection2Description"
            label="Section #2 Description"
            multiline
            fullWidth
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSubsection2ImageUpload"
              label="Section #2 Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSubsection2ImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSubsection2ImageUpload")}
            </ImageInput>
          </div>
        </FormTab>

        <FormTab label="Brands">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="brandsSectionTitle"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            source="brandsSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <ResourceEditor
            resourceId={"partnership-page-brands"}
            createNewLabel="Add New Brand"
            updatedOrderMsg="Brand order updated."
            updatingOrderMsg="Updating brand order..."
            heading="Brands"
            renderName={(resource: any) => (
              <img src={resource.imageUpload.uri} width="auto" height="32" />
            )}
            CreateScreen={PartnershipBrandsCreate}
            EditScreen={PartnershipBrandsEdit}
            hasOrder={false}
            hasEnabled={false}
          />
        </FormTab>

        <FormTab label="Sections">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="sectionsTitle"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            source="sectionsDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <ResourceEditor
            resourceId={"partnership-page-sections"}
            createNewLabel="Add New Section"
            updatedOrderMsg="Section order updated."
            updatingOrderMsg="Updating section order..."
            heading="Sections"
            renderName={(resource: any) =>
              `${resource.title.substr(0, 100)}...`
            }
            CreateScreen={PartnershipSectionsCreate}
            EditScreen={PartnershipSectionsEdit}
            hasOrder={false}
            hasEnabled={false}
          />
        </FormTab>
      </TabbedForm>
    </RaEditWrapper>
  );
};
